import {
	Fragment,
	useCallback,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { playSound, Sound } from '~/assets/sounds';
import { CurrencyIcon } from '~/components/collections';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { useWalletIcon, useZoomFactor } from '~/hooks';
import { rage } from '~/rage';
import { useObservable } from '~/rage/reactHooks';
import { Currency } from '~/shared';
import { Popup } from '../Popup';
import { ITaskProps } from './Task.model';
import {
	StyledBackground,
	StyledBottomContainer,
	StyledContainer,
	StyledControlsContainer,
	StyledDot,
	StyledIconButtonWrapper,
	StyledRefresh,
	StyledRewardContainer,
	StyledRewardsContainer,
	StyledStar,
	StyledTopContainer,
	StyledXPStar,
} from './Task.styled';

const POPUP_APPROX_HEIGHT = 36;
const POPUP_GAP = 8;

const Task = ({
	id,
	name,
	text,
	exp,
	amount,
	progress,
	type,
	fixedWidth,
	span,
	refresh,
	onRefresh,
}: ITaskProps) => {
	const [popupVisible, setPopupVisible] = useState<boolean>(false);
	const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
	const starButtonRef = useRef<HTMLDivElement>(null);
	const zoomFactor = useZoomFactor();
	const tasks = useObservable(rage.dailyTasks.getTasks());
	const isPinned = tasks.find((t) => t.id === id)?.isPinned ?? false;
	const rubleIcon = useWalletIcon(Currency.Ruble, true);

	const updatePopupPosition = useCallback(() => {
		if (!starButtonRef.current) return;

		const rect = starButtonRef.current.getBoundingClientRect();
		const z = zoomFactor;
		const viewportHeight = window.innerHeight / z;
		const approximateTop = rect.top / z - POPUP_APPROX_HEIGHT - POPUP_GAP;
		const clampedTop = Math.max(
			8,
			Math.min(approximateTop, viewportHeight - POPUP_APPROX_HEIGHT - 8),
		);

		const anchorCenterX = (rect.left + rect.width / 2) / z;

		setPopupPosition({
			top: clampedTop,
			left: anchorCenterX,
		});
	}, [zoomFactor]);

	useLayoutEffect(() => {
		if (!popupVisible) return;

		updatePopupPosition();

		const handleUpdate = () => {
			updatePopupPosition();
		};

		window.addEventListener('scroll', handleUpdate, true);
		window.addEventListener('resize', handleUpdate);

		return () => {
			window.removeEventListener('scroll', handleUpdate, true);
			window.removeEventListener('resize', handleUpdate);
		};
	}, [popupVisible, updatePopupPosition]);

	return (
		<Fragment>
			{createPortal(
				<CSSTransition
					in={popupVisible}
					classNames={'popup'}
					timeout={500}
					unmountOnExit
				>
					<Popup
						text={isPinned ? 'Открепить задачу' : 'Закрепить задачу'}
						style={{
							position: 'fixed',
							zIndex: 10000,
							pointerEvents: 'none',
							top: popupPosition.top,
							left: popupPosition.left,
							transform: 'translateX(-50%)',
						}}
					/>
				</CSSTransition>,
				document.body,
			)}
			<StyledContainer $fixedWidth={fixedWidth} $span={span}>
				<StyledBackground $progress={progress} />
				<StyledTopContainer>
					<Text style={{ fontSize: '16px', fontWeight: 500, color: '#FFFFFF' }}>
						{name}{' '}
						<span style={{ fontWeight: 400, opacity: 0.5 }}>
							{type === 'daily' ? '(Ежедневные задачи)' : '(Основная задача)'}
						</span>
					</Text>
					<StyledControlsContainer>
						{refresh && (
							<Fragment>
								<StyledIconButtonWrapper
									onClick={() => {
										onRefresh?.();
										playSound(Sound.ClickButton);
									}}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<StyledRefresh />
								</StyledIconButtonWrapper>
								<StyledDot />
							</Fragment>
						)}
						<StyledIconButtonWrapper
							ref={starButtonRef}
							onMouseEnter={() => {
								updatePopupPosition();
								setPopupVisible(true);
								playSound(Sound.LinkCursor);
							}}
							onClick={() => {
								rage.dailyTasks.setIsPinned(id, !isPinned);
								playSound(Sound.ClickButton);
							}}
							onMouseLeave={() => setPopupVisible(false)}
						>
							<StyledStar $pinned={isPinned} />
						</StyledIconButtonWrapper>
					</StyledControlsContainer>
				</StyledTopContainer>
				<Text>{text}</Text>
				<Divider $direction="H" />
				<StyledBottomContainer>
					<Text
						style={{
							fontSize: '16px',
							fontWeight: 400,
							color: '#FFFFFF',
							opacity: 0.25,
						}}
					>
						Награда за выполнение
					</Text>
					<StyledRewardsContainer>
						<StyledRewardContainer>
							<Text style={{ color: '#FFB500' }}>
								{exp.toLocaleString('ru-RU')}
							</Text>
							<StyledXPStar />
						</StyledRewardContainer>
						<StyledDot />
						<StyledRewardContainer>
							<Text style={{ color: '#FFFFFF' }}>
								{amount.toLocaleString('ru-RU')}
							</Text>
							<CurrencyIcon
								icon={rubleIcon}
								size="sm"
								style={{ opacity: 0.5 }}
							/>
						</StyledRewardContainer>
					</StyledRewardsContainer>
				</StyledBottomContainer>
			</StyledContainer>
		</Fragment>
	);
};

export { Task };
